// components/team-helpers.js: #8b pure logic for the Team A/B lobby
// columns. Extracted so node:test (which doesn't transform JSX) can cover
// the bucketing + enable/disable truth table directly; TeamColumns.jsx
// consumes these.
//
// Server contract (#8a): every snapshot occupant carries
// `team: null | 'A' | 'B'`. Max 2 occupants per team.

export const MAX_PER_TEAM = 2;

/**
 * Stable comparator: by seat_index when both have one, else preserve the
 * incoming (join) order. Array#sort is stable in V8, so equal keys keep
 * their relative order without an explicit tiebreak.
 *
 * @param {{ seat_index?: number|null }} a
 * @param {{ seat_index?: number|null }} b
 * @returns {number}
 */
function bySeatThenJoin(a, b) {
  const sa = a.seat_index;
  const sb = b.seat_index;
  if (typeof sa === 'number' && typeof sb === 'number') return sa - sb;
  return 0;
}

/**
 * Bucket occupants into the two team columns + the unteamed pool.
 * `team === 'A'|'B'` lands in that column; anything else (null/undefined)
 * is unteamed. Each bucket keeps a stable order (seat_index then join).
 *
 * @template {{ occupant_id: string, team?: 'A'|'B'|null, seat_index?: number|null }} T
 * @param {T[]} occupants
 * @returns {{ A: T[], B: T[], unteamed: T[] }}
 */
export function teamColumns(occupants) {
  const A = [];
  const B = [];
  const unteamed = [];
  for (const o of occupants || []) {
    if (o.team === 'A') A.push(o);
    else if (o.team === 'B') B.push(o);
    else unteamed.push(o);
  }
  A.sort(bySeatThenJoin);
  B.sort(bySeatThenJoin);
  unteamed.sort(bySeatThenJoin);
  return { A, B, unteamed };
}

/**
 * Is `team` at capacity (MAX_PER_TEAM occupants)?
 *
 * @param {Array<{ team?: 'A'|'B'|null }>} occupants
 * @param {'A'|'B'} team
 * @returns {boolean}
 */
export function teamIsFull(occupants, team) {
  let count = 0;
  for (const o of occupants || []) {
    if (o.team === team) count += 1;
  }
  return count >= MAX_PER_TEAM;
}

/**
 * Can the local occupant (myOccupantId) join `team`? False when the team is
 * full, or when I'm already on it (re-picking the same team is a server
 * no-op). My own slot doesn't count against capacity for the team I sit on,
 * so a member of a full team can't "join" it again, and a 1-of-2 member
 * isn't blocked from their own team (they just can't re-join it).
 *
 * @param {Array<{ occupant_id: string, team?: 'A'|'B'|null }>} occupants
 * @param {string|null} myOccupantId
 * @param {'A'|'B'} team
 * @returns {boolean}
 */
export function canJoinTeam(occupants, myOccupantId, team) {
  const me = (occupants || []).find((o) => o.occupant_id === myOccupantId);
  if (me && me.team === team) return false; // already on it → no-op
  return !teamIsFull(occupants, team);
}

/**
 * Human-readable label for a team id.
 *
 * B3b D5: always the structural name ("Team A" / "Team B"), even after
 * the viewer picks a side. The viewer's own column gets a separate
 * "your team" tag in the JSX (TeamColumns) instead of renaming the
 * column, so the names never flip mid-session.
 *
 * @param {'A'|'B'} team
 * @param {boolean|null} [isYours]
 * @returns {string}
 */
export function teamLabel(team, isYours = null) {
  void isYours;
  return `Team ${team}`;
}

/**
 * B4a-A9: the roster heading names what it counts. The number is people
 * in the room, not teams, so the heading reads "Players (N)".
 *
 * @param {number} occupantCount
 * @returns {string}
 */
export function rosterHeading(occupantCount) {
  return `Players (${occupantCount})`;
}
